import { Injectable } from '@angular/core'
import { combineLatest, filter, firstValueFrom, switchMap, take } from 'rxjs'
import { tap } from 'rxjs/operators'

import { AuthService } from '../../shared/services/auth.service'
import { BalanceService } from '../../shared/services/balance.service'
import { BetService } from '../../shared/services/bet.service'
import { HelperService } from '../../shared/services/helper.service'
import { LiveUpdateService } from '../../shared/services/live-update.service'
import { WebsocketService } from '../../shared/services/websocket.service'
import { BetSlipSetting } from '../../shared/types/auth.type'

@Injectable({
	providedIn: 'root',
})
export class AuthenticatedLayoutService {
	constructor(
		private readonly helperService: HelperService,
		private readonly balanceService: BalanceService,
		private readonly authService: AuthService,
		private readonly websocketService: WebsocketService,
		private readonly liveUpdateService: LiveUpdateService,
		private readonly betService: BetService,
	) {}

	initializeApp() {
		this.initWebsocketConnection()
		this.balanceService.getBalance()
		this.subscribeToWebsocketEvents()
		this.refreshExposureOnWebsocketConnection()
		this.onUserOnline()
	}

	onUserOnline = () => {
		window.addEventListener('online', () => {
			setTimeout(() => {
				this.refreshExposureOnWebsocketConnection()
			}, 2000)
		})
	}

	private initWebsocketConnection() {
		// Initial connection
		this.websocketService.startConnection().subscribe({
			error: (error) => console.error('Initial WebSocket connection failed:', error),
		})

		// Auto-reconnection when connection is lost
		this.websocketService.connectionStatus$
			.pipe(
				filter((status) => !status && this.authService.getAccessToken() !== ''),
				switchMap(() => this.websocketService.startConnection()),
			)
			.subscribe({
				error: (error) => console.error('WebSocket reconnection failed:', error),
			})
	}

	private subscribeToWebsocketEvents() {
		const userData = this.helperService.getUserData()

		this.websocketService.connectionStatus$
			.pipe(
				filter((status) => status && Boolean(userData)),
				switchMap(() => this.liveUpdateService.getData<{ type: string; markets: string; data: unknown }>()),
				// debounceTime(WEBSOCKET_DEBOUNCE_MILLISECONDS),
			)
			.subscribe({
				next: (update) => {
					if (update?.type === 'wl_betslip' && update.markets && userData?.user_fields?.DOMAIN_NAME) {
						const parsedData = JSON.parse(update.markets) as { betslip: BetSlipSetting; domain_name: string }
						this.helperService.setUserData({ ...userData, user_fields: { ...userData.user_fields, WHITE_LABEL_BET_SLIP: parsedData.betslip } })
					}

					if (update?.type === 'wallet') {
						const data = update.data as { balance: string; risk_exposure: string; liability: number; total_exposure: string }

						this.balanceService.balanceData$.next({
							...this.balanceService.balanceData$.getValue()!,
							BALANCE: data.balance,
							RISK_EXPOSURE: data.risk_exposure,
							LIABILITY: data.liability,
							TOTAL_EXPOSURE: data.total_exposure,
						})

						// Set flag to indicate balance came from WebSocket
						this.balanceService.isExposureFromWebSocket$.next(true)

						firstValueFrom(this.betService.fetchCurrentUserOrders())
					}

					if (update?.type === 'logout') {
						this.authService.logOut()
					}
				},
				error: (error) => {
					console.error('WebSocket error:', error)
					this.initializeApp()
				},
			})
	}

	private refreshExposureOnWebsocketConnection() {
		combineLatest([this.balanceService.balanceData$, this.websocketService.connectionStatus$])
			.pipe(
				filter(([balanceData, status]) => status && Boolean(balanceData)),
				take(1),
				switchMap(([balanceData]) => this.balanceService.refreshExposure(balanceData!.ID.toString())),
				tap(() => (this.balanceService.isFetchingBalance = false)),
			)
			.subscribe()
	}
}
